(ns frontend.view.entity-viewers.modules.risk.event
  (:require
   [ajax.core :as ajax]
   [clojure.data :as data]
   [clojure.set :as set]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.time :as c-time]
   [frontend.actions.entity :as entity-actions]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.view.components.spinner :as spinner]
   [frontend.view.modals.entity-audit-trail.view :as audit-trail-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.entity-viewers.modules.risk.sub :as sub]
   [frontend.view.modals.risk-editor.view :as risk-editor]
   [frontend.tenant-customizations :as tenant-customizations]
   [re-frame.core :as rf]
   [reagent.core :as r]))


(rf/reg-event-fx
 ::clear-loading
 (fn [{db :db} [_ entity-id]]
   {:db (-> db
            (assoc-in [::sub/risks-loading? entity-id] false))}))

(rf/reg-event-fx
 ::load-risks-ok
 (fn [{db :db} [_ entity-id resp]]
   (let [{:keys [risks risk-attachments]} (:body resp)
         attachments-by-id (zipmap (->> risk-attachments
                                        (mapv :db/id))
                                   risk-attachments)]
     (swap! session/entities merge attachments-by-id)
     (deref session/entities)
     {:db (-> db
              (assoc-in [::sub/risks entity-id] risks))
      :fx [[:dispatch-later
            {:ms       (max 10
                            (- spinner/min-spinner-timeout
                               (- (c-time/now-millis)
                                  (get-in db [::sub/load-started entity-id] 0))))
             :dispatch [::clear-loading entity-id]}]]})))

(rf/reg-event-fx
 ::load-risks-failed
 (fn [{db :db} [_ entity-id resp]]
   {:db (-> db
            (assoc-in [::sub/risks-loading? entity-id] false))}))

(rf/reg-event-fx
 ::reload-risks
 (fn [{db :db} [_ entity-id]]
   {:http-xhrio {:method          :post
                 :uri             "/risk/api/get-risks"
                 :params          {:containing-entity entity-id}
                 :timeout         30000
                 :format          (ajax/transit-request-format)
                 :response-format (ajax/transit-response-format)
                 :on-success      [::load-risks-ok entity-id]
                 :on-failure      [::load-risks-failed entity-id]}}))

(rf/reg-event-fx
 ::load-risks
 (fn [{db :db} [_ entity-id]]
   {:db (-> db
            (assoc-in [::sub/risks-loading? entity-id] true)
            (assoc-in [::sub/load-started entity-id] (c-time/now-millis)))
    :fx [[:dispatch [::reload-risks entity-id]]]}))


(rf/reg-event-fx
 ::collapse-all-risks
 (fn [{db :db} [_ entity-id]]
   {:db (-> db
            (assoc-in [::sub/all-risks-collapse-status entity-id] :collapsed)
            (assoc-in [::sub/risk-collapsed? entity-id]
                      (->> db
                           (get-in [::sub/risks entity-id])
                           (map (fn [r]
                                  [(:db/id r) :collapsed]))
                           (into {}))))}))

(rf/reg-event-fx
 ::expand-all-risks
 (fn [{db :db} [_ entity-id]]
   (let [risks (get-in db [::sub/risks entity-id])]
     {:db (-> db
              (assoc-in [::sub/all-risks-collapse-status entity-id] :expanded)
              (assoc-in [::sub/risk-collapsed? entity-id]
                        (->> risks
                             (map (fn [r]
                                    [(:db/id r) :expanded]))
                             (into {}))))})))

(rf/reg-event-fx
 ::expand-risk
 (fn [{db :db} [_ entity-id risk-id]]
   {:db (-> db
            (assoc-in [::sub/all-risks-collapse-status entity-id] :mixed)
            (assoc-in [::sub/risk-collapsed? entity-id risk-id] :expanded))}))

(rf/reg-event-fx
 ::collapse-risk
 (fn [{db :db} [_ entity-id risk-id]]
   {:db (-> db
            (assoc-in [::sub/all-risks-collapse-status entity-id] :mixed)
            (assoc-in [::sub/risk-collapsed? entity-id risk-id] :collapsed))}))


(def default-mandatory-fields
  #{:risk/title
    :risk/status
    :risk/severity
    :risk-holding/category})


(defn resolve-mandatory-fields
  [risk-entity-type]
  (cond
    (and (tenant-customizations/capman?)
         (= risk-entity-type :entity.type/risk-holding))
    (let [schema (registry/get-schema @session/tenant-schema :entity.type/risk-holding)
          mandatory-custom-field-labels-lc #{"impact" "propability" "probability"}
          mandatory-custom-fields
          (->> schema
               (keep (fn [[field field-schema]]
                       (let [label (loc/field-name field field-schema)
                             label-lc (string/lower-case (or label ""))]
                         (when (some #{label-lc} mandatory-custom-field-labels-lc)
                           field))))
               (set))
          mandatory-fields (vec (set/union default-mandatory-fields mandatory-custom-fields))]
      mandatory-fields)

    :else
    default-mandatory-fields))

(rf/reg-event-fx
 ::open-risk
 (fn [{db :db} [_ entity-id risk-entity]]
   (let [state (r/atom risk-entity)]
     (modals/open-modal!
      risk-editor/modal-key
      {:entity-id            (:db/id risk-entity)
       :containing-entity-id entity-id
       :entity-state         state
       :on-ok!               (fn [risk-id close!]
                               (let [current-vals risk-entity
                                     new-vals (-> @state
                                                  (dissoc :common/attachments :uploading))
                                     [removals changes _] (data/diff current-vals new-vals)
                                     changes (merge {}
                                                    (->> (concat (map first removals)
                                                                 (map first changes))
                                                         (set)
                                                         (remove (fn [k]
                                                                   (some #{k}
                                                                         [:risk/primary-entity :common/attachments
                                                                          :common/containing-entity])))
                                                         (map (fn [k]
                                                                ;; ensure multi values are set properly
                                                                [k (get new-vals k)]))
                                                         (into {})))]
                                 (entity-actions/*->entity-update!
                                  risk-id
                                  changes
                                  (fn [resp]
                                    (rf/dispatch [::reload-risks entity-id])
                                    (close!))
                                  (fn [err]
                                    (logging/error "Failed to update risk" err)))))
       :on-remove!           (fn [risk-id close!]
                               (entity-actions/*->disable!
                                risk-id
                                (fn [resp]
                                  (rf/dispatch [::reload-risks entity-id])
                                  (close!))
                                (fn [err]
                                  (logging/error "Failed to remove risk" err))))
       :skip-success-msg?    true
       :prevent-backend-update? true
       :title-text           (loc/tr :edit-risk)
       :mandatory-fields     (resolve-mandatory-fields (:entity/type risk-entity))}))))


(rf/reg-event-fx
 ::open-risk-history
 (fn [{db :db} [_ entity-id risk-entity]]
   (modals/open-modal!
    audit-trail-modal/modal-key
    {:title (loc/tr :audit-trail [(entity/display-string risk-entity)])
     :entity-ids [(:db/id risk-entity)]
     :primary-entity-id (:db/id risk-entity)
     :primary-entity-type (:entity/type risk-entity)})
   {}
 ))

(rf/reg-event-fx
 ::create-risk
 (fn [{db :db} [_ entity-id]]
   (let [state (r/atom {:entity/type :entity.type/risk-holding
                        :common/containing-entity entity-id})]
     (modals/open-modal!
      risk-editor/modal-key
      {:entity-id         entity-id
       :containing-entity-id entity-id
       :entity-state      state
       :on-ok!            (fn [passed-id close!]
                            (let [data @state]
                              (entity-actions/*->create
                               @state
                               (fn [resp]
                                 (rf/dispatch [::reload-risks entity-id])
                                 (close!))
                               (fn [err]
                                 (logging/error "Failed to update risk" err)))))
       :skip-success-msg? true
       :prevent-backend-update? true
       :title-text        (loc/tr :create-risk)
       :mandatory-fields  (resolve-mandatory-fields (:entity/type @state))}))))
